import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Tooltip,
  TablePagination
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

import {
  getActivationTokens,
  getActivationToken,
  createActivationToken,
  updateActivationToken,
  deleteActivationToken,
  getSubscriptionPlans
} from '../services/subscriptionService';
import { ActivationToken, SubscriptionPlan } from '../types';

interface TokenFormData {
  plan_id: number;
  subscription_type: 'term' | 'year';
  expires_at: Dayjs | null;
}

// Helper function to format dates
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const TokenManagement: React.FC = () => {
  const [tokens, setTokens] = useState<ActivationToken[]>([]);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingToken, setEditingToken] = useState<ActivationToken | null>(null);
  const [formData, setFormData] = useState<TokenFormData>({
    plan_id: 0,
    subscription_type: 'term',
    expires_at: null
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalTokens, setTotalTokens] = useState(0);

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tokensData, plansData] = await Promise.all([
        getActivationTokens({ skip: page * rowsPerPage, limit: rowsPerPage }),
        getSubscriptionPlans()
      ]);
      setTokens(tokensData);
      setPlans(plansData as SubscriptionPlan[]);
      setTotalTokens(tokensData.length); // In a real app, you'd get total count from API
    } catch (error) {
      console.error('Error fetching data:', error);
      showSnackbar('Error fetching data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenDialog = (token?: ActivationToken) => {
    if (token) {
      setEditingToken(token);
      setFormData({
        plan_id: token.plan_id,
        subscription_type: token.subscription_type,
        expires_at: token.expires_at ? dayjs(token.expires_at) : null
      });
    } else {
      setEditingToken(null);
      setFormData({
        plan_id: plans[0]?.id || 0,
        subscription_type: 'term',
        expires_at: null
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingToken(null);
    setFormData({
      plan_id: 0,
      subscription_type: 'term',
      expires_at: null
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingToken) {
        await updateActivationToken(editingToken.id, {
          plan_id: formData.plan_id,
          subscription_type: formData.subscription_type,
          expires_at: formData.expires_at ? formData.expires_at.toISOString() : undefined
        });
        showSnackbar('Token updated successfully', 'success');
      } else {
        await createActivationToken({
          plan_id: formData.plan_id,
          subscription_type: formData.subscription_type,
          expires_at: formData.expires_at ? formData.expires_at.toISOString() : undefined
        });
        showSnackbar('Token created successfully', 'success');
      }
      handleCloseDialog();
      fetchData();
    } catch (error) {
      console.error('Error saving token:', error);
      showSnackbar('Error saving token', 'error');
    }
  };

  const handleDelete = async (tokenId: number) => {
    if (!window.confirm('Are you sure you want to delete this token?')) return;
    
    try {
      await deleteActivationToken(tokenId);
      showSnackbar('Token deleted successfully', 'success');
      fetchData();
    } catch (error) {
      console.error('Error deleting token:', error);
      showSnackbar('Error deleting token', 'error');
    }
  };

  const copyToClipboard = (text: string) => {
    if (navigator && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      navigator.clipboard.writeText(text)
        .then(() => showSnackbar('Token copied to clipboard', 'info'))
        .catch(() => showSnackbar('Failed to copy token', 'error'));
    } else {
      // Fallback for older browsers
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        showSnackbar('Token copied to clipboard', 'info');
      } catch (err) {
        showSnackbar('Failed to copy token', 'error');
      }
    }
  };

  const getStatusChip = (token: ActivationToken) => {
    if (token.is_used) {
      return <Chip label="Used" color="default" size="small" />;
    }
    if (token.expires_at && new Date(token.expires_at) < new Date()) {
      return <Chip label="Expired" color="error" size="small" />;
    }
    return <Chip label="Active" color="success" size="small" />;
  };

  const getPlanName = (planId: number) => {
    const plan = plans.find(p => p.id === planId);
    return plan?.name || 'Unknown Plan';
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2">
            Activation Token Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Create Token
          </Button>
        </Box>

        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Token</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Used By</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tokens.map((token) => (
                  <TableRow key={token.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {token.token.substring(0, 8)}...
                        </Typography>
                        <Tooltip title="Copy token">
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard(token.token)}
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>{getPlanName(token.plan_id)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={token.subscription_type} 
                        variant="outlined" 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>{getStatusChip(token)}</TableCell>
                    <TableCell>
                      {formatDate(token.created_at)}
                    </TableCell>
                    <TableCell>
                      {token.expires_at 
                        ? formatDate(token.expires_at)
                        : 'Never'
                      }
                    </TableCell>
                    <TableCell>
                      {token.used_by_school_id ? `School ${token.used_by_school_id}` : '-'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View details">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(token)}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {!token.is_used && (
                          <>
                            <Tooltip title="Edit token">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog(token)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete token">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(token.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalTokens}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>

        {/* Create/Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingToken ? 'Edit Token' : 'Create New Token'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Subscription Plan</InputLabel>
                <Select
                  value={formData.plan_id}
                  onChange={(e) => setFormData({ ...formData, plan_id: e.target.value as number })}
                  label="Subscription Plan"
                >
                  {plans.map((plan) => (
                    <MenuItem key={plan.id} value={plan.id}>
                      {plan.name} - KES {plan.price_per_term}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Subscription Type</InputLabel>
                <Select
                  value={formData.subscription_type}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    subscription_type: e.target.value as 'term' | 'year' 
                  })}
                  label="Subscription Type"
                >
                  <MenuItem value="term">Term</MenuItem>
                  <MenuItem value="year">Year</MenuItem>
                </Select>
              </FormControl>

              <DateTimePicker
                label="Expires At (Optional)"
                value={formData.expires_at}
                onChange={(newValue) => setFormData({ 
                  ...formData, 
                  expires_at: newValue ? dayjs(newValue) : null 
                })}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: 'Leave empty for no expiration'
                  }
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained">
              {editingToken ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default TokenManagement; 